import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Package, MagnifyingGlass, Trash, DownloadSimple, CurrencyInr, Warning, ArrowUp, ArrowDown, Clock, PencilSimple } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth, isAdmin } from "@/lib/access";
import { getErrorMessage } from "@/lib/auth";

const API = `${window.location.origin}/api`;
const formatCurrency = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v);
const ENTRY_TYPES = [
  { value: "purchase", label: "Purchase", color: "bg-green-100 text-green-700 border-green-200" },
  { value: "sale", label: "Sale", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "return", label: "Return", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "wastage", label: "Wastage", color: "bg-red-100 text-red-700 border-red-200" },
];
function getEntryTypeConfig(type) {
  return ENTRY_TYPES.find(t => t.value === type) || ENTRY_TYPES[0];
}

/* ====== Dashboard Cards ====== */
function DashboardCards({ data, onCardClick, showValue }) {
  const cards = [
    { key: "all", label: "Total Products", value: data.total_products, icon: Package, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
    { key: "all", label: "Total Stock", value: `${data.total_stock_kg} KG`, icon: Package, color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
    { key: "low", label: "Low Stock", value: data.low_stock_count, icon: Warning, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
    showValue && { key: "all", label: "Total Value", value: formatCurrency(data.total_value), icon: CurrencyInr, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
  ].filter(Boolean);
  return (
    <div className={`grid grid-cols-2 ${showValue ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-2 sm:gap-3`} data-testid="inventory-cards">
      {cards.map((c) => (
        <button key={c.label} onClick={() => onCardClick && onCardClick(c.key)} className={`text-left p-3 border-2 rounded-xl ${c.border} bg-white hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer`} data-testid={`inv-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}><c.icon size={18} className={c.color} weight="bold" /></div>
            <div className="min-w-0 flex-1">
              <p className={`text-[11px] font-bold uppercase tracking-wide ${c.color}`}>{c.label}</p>
              <p className={`text-xl font-black truncate ${c.color}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.value}</p>
            </div>
          </div>
          <p className={`text-[10px] mt-1 font-semibold ${c.color}`}>VIEW →</p>
        </button>
      ))}
    </div>
  );
}

/* ====== Stock Card (mobile) ====== */
function MinKgInput({ item, onSaved }) {
  const current = item.min_kg_override ?? "";
  const [val, setVal] = useState(current);
  useEffect(() => { setVal(item.min_kg_override ?? ""); }, [item.min_kg_override]);
  const save = async () => {
    if (String(val) === String(current)) return;
    const next = val === "" ? null : Number(val);
    if (next !== null && (Number.isNaN(next) || next < 0)) { toast.error("Enter a valid minimum KG"); setVal(current); return; }
    try {
      await axios.put(`${API}/inventory/min-level`, { product_name: item.product_name, min_kg: next });
      toast.success(next === null ? `${item.product_name}: using default minimum` : `${item.product_name}: alert below ${next} KG`);
      onSaved();
    } catch (err) { toast.error(getErrorMessage(err)); setVal(current); }
  };
  return (
    <Input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      placeholder={`${item.min_kg ?? 10}`} title={item.min_kg_override == null ? "Using default minimum (Settings)" : "Custom minimum"}
      className={`h-7 w-20 ml-auto text-xs text-right font-mono ${item.min_kg_override == null ? "" : "border-indigo-300 bg-indigo-50"}`}
      data-testid={`min-kg-${item.product_name}`} />
  );
}

function StockCard({ item, onReduce, onEdit, onDelete, admin }) {
  const isLow = item.is_low ?? item.available_kg < 10;
  return (
    <Card className={`p-3 border bg-white ${isLow ? "border-red-200" : "border-gray-200"}`} data-testid={`stock-card-${item.product_name}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm text-gray-900 truncate">{item.product_name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-base font-bold ${isLow ? "text-red-600" : "text-green-600"}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{item.available_kg} KG</span>
            {isLow && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold border border-red-200">LOW</span>}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {admin && <p className="text-xs text-gray-500">Avg Price</p>}
          {admin && <p className="text-sm font-bold text-gray-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(item.avg_kilo_price)}/kg</p>}
          {item.last_purchase_date && <p className="text-[10px] text-gray-400 mt-0.5">{item.last_purchase_date}</p>}
        </div>
      </div>
      {admin && (
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-500">Value: <b className="text-gray-900">{formatCurrency(item.total_value)}</b></span>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600" onClick={() => onEdit(item)} data-testid={`edit-stock-${item.product_name}`}><PencilSimple size={14} /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(item)} data-testid={`delete-stock-${item.product_name}`}><Trash size={14} /></Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => onReduce(item)} data-testid={`reduce-${item.product_name}`}>
            <ArrowDown size={12} className="mr-0.5" /> Reduce
          </Button>
        </div>
      </div>
      )}
    </Card>
  );
}

/* ====== Main Page ====== */
export default function InventoryPage() {
  const admin = isAdmin(useAuth());
  const [dashboard, setDashboard] = useState(null);
  const [stock, setStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showLowStock, setShowLowStock] = useState(false);
  const [showReduceDialog, setShowReduceDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyProduct, setHistoryProduct] = useState("");
  const [reduceProduct, setReduceProduct] = useState("");
  const [reduceKg, setReduceKg] = useState("");
  const [reduceNotes, setReduceNotes] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const [editForm, setEditForm] = useState({ product_name: "", kg: "", value: "", kilo_price: "", date: "", notes: "", entry_type: "purchase", batch_code: "", supplier: "", expiry_date: "", remarks: "" });
  const [editManualProduct, setEditManualProduct] = useState(false);
  const [batches, setBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);
  const [reduceBatch, setReduceBatch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, stockRes, prodRes, batchRes] = await Promise.all([
        axios.get(`${API}/inventory/dashboard`),
        axios.get(`${API}/inventory/stock`, { params: { search: search || undefined, low_stock: showLowStock || undefined } }),
        axios.get(`${API}/products`),
        axios.get(`${API}/inventory/batches`),
      ]);
      setDashboard(dashRes.data);
      setStock(stockRes.data);
      setProducts(prodRes.data);
      setBatches(batchRes.data);
    } catch (err) { toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  }, [search, showLowStock]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReduce = async () => {
    if (!reduceProduct) { toast.error("Select product"); return; }
    const rkg = parseFloat(reduceKg) || 0;
    if (rkg <= 0) { toast.error("Enter KG"); return; }
    try {
      await axios.post(`${API}/inventory/reduce`, { product_name: reduceProduct, kg: rkg, notes: reduceNotes || "Manual reduction", batch_code: reduceBatch === "fifo" ? "" : reduceBatch });
      toast.success(`${rkg} KG reduced from ${reduceProduct}`);
      setShowReduceDialog(false);
      setReduceProduct(""); setReduceKg(""); setReduceNotes(""); setReduceBatch("");
      fetchData();
    } catch (err) { toast.error("Failed to reduce stock"); }
  };

  const openReduce = (item) => { setReduceProduct(item.product_name); setReduceKg(""); setReduceNotes(""); setReduceBatch(""); setShowReduceDialog(true); };

  const openHistory = async (productName) => {
    setHistoryProduct(productName || "");
    try {
      const params = {};
      if (productName) params.product_name = productName;
      const res = await axios.get(`${API}/inventory/history`, { params });
      setHistory(res.data);
      setShowHistory(true);
    } catch (err) { toast.error("Failed to load history"); }
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      product_name: entry.product_name,
      kg: String(entry.kg),
      value: String(entry.value || 0),
      kilo_price: String(entry.kilo_price || 0),
      date: entry.date || "",
      notes: entry.notes || "",
      entry_type: entry.type || "purchase",
      batch_code: entry.batch_code || "",
      supplier: entry.supplier || "",
      expiry_date: entry.expiry_date || "",
      remarks: "",
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editEntry) return;
    try {
      await axios.put(`${API}/inventory/${editEntry.entry_id}`, {
        product_name: editForm.product_name,
        kg: parseFloat(editForm.kg) || 0,
        value: parseFloat(editForm.value) || 0,
        kilo_price: parseFloat(editForm.kilo_price) || 0,
        date: editForm.date,
        notes: editForm.notes,
        entry_type: editForm.entry_type,
        batch_code: editForm.batch_code,
        supplier: editForm.supplier,
        expiry_date: editForm.expiry_date,
        remarks: editForm.remarks,
      });
      toast.success("Entry updated!");
      setShowEditDialog(false);
      setEditEntry(null);
      fetchData();
      if (showHistory) openHistory(historyProduct);
    } catch (err) { toast.error("Update failed"); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/inventory/${entryId}`);
      toast.success("Entry deleted");
      fetchData();
      if (showHistory) openHistory(historyProduct);
    } catch (err) { toast.error("Delete failed"); }
  };

  // For stock-level delete: open history to delete individual entries
  const handleStockEdit = (item) => { openHistory(item.product_name); };
  const handleStockDelete = async (item) => {
    if (!window.confirm(`Delete ALL entries for ${item.product_name}? This will remove all stock data for this product.`)) return;
    try {
      const res = await axios.get(`${API}/inventory/history`, { params: { product_name: item.product_name } });
      for (const entry of res.data) {
        await axios.delete(`${API}/inventory/${entry.entry_id}`);
      }
      toast.success(`All entries for ${item.product_name} deleted`);
      fetchData();
    } catch (err) { toast.error("Delete failed"); }
  };

  const filteredStock = useMemo(() => stock, [stock]);

  return (
    <div className="space-y-4 max-w-7xl" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Inventory</h1>
          <p className="text-xs font-bold text-indigo-600">{stock.length} products tracked</p>
        </div>
        <div className="flex gap-2">
          {admin && (
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => openHistory("")} data-testid="history-btn">
            <Clock size={14} className="mr-1" /> History
          </Button>
          )}
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => setShowBatches(true)} data-testid="batches-btn">
            <Package size={14} className="mr-1" /> Batches
          </Button>
          {admin && (
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => window.open(`${API}/inventory/export`, "_blank")} data-testid="export-inventory-btn">
            <DownloadSimple size={14} className="mr-1" /> Export
          </Button>
          )}
        </div>
      </div>

      {dashboard && <DashboardCards data={dashboard} showValue={admin} onCardClick={(k) => { setShowLowStock(k === "low"); }} />}

      {/* Filters */}
      <div className="flex gap-2 items-center overflow-x-auto mobile-scroll-x">
        <div className="relative flex-1 min-w-[150px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search product..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="inventory-search" />
        </div>
        <Button variant={showLowStock ? "default" : "outline"} size="sm" className={`h-9 text-xs flex-shrink-0 ${showLowStock ? "bg-red-600 text-white hover:bg-red-700" : ""}`} onClick={() => setShowLowStock(!showLowStock)} data-testid="low-stock-filter">
          <Warning size={14} className="mr-1" /> Low Stock
        </Button>
      </div>

      {/* Stock View */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={`skel-${i}`} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : filteredStock.length === 0 ? (
        <Card className="p-8 text-center border border-gray-200 bg-white" data-testid="empty-stock">
          <Package size={40} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No stock data yet. Add your first purchase!</p>
        </Card>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="sm:hidden space-y-2" data-testid="stock-cards-mobile">
            {filteredStock.map((item) => <StockCard key={item.product_name} item={item} admin={admin} onReduce={openReduce} onEdit={handleStockEdit} onDelete={handleStockDelete} />)}
          </div>
          {/* Desktop Table */}
          <Card className="hidden sm:block border border-gray-200 bg-white overflow-hidden" data-testid="stock-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Product Name</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Available KG</th>
                    {admin && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Avg Price/KG</th>}
                    {admin && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total Value</th>}
                    {admin && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600" title="Alert when stock drops below this">Min KG</th>}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Purchase</th>
                    {admin && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStock.map((item) => {
                    const isLow = item.is_low ?? item.available_kg < 10;
                    return (
                      <tr key={item.product_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-gray-900">{item.product_name}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold font-mono ${isLow ? "text-red-600" : "text-green-600"}`}>{item.available_kg}</span>
                          {isLow && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-bold">LOW</span>}
                        </td>
                        {admin && <td className="px-4 py-3 text-right font-mono text-gray-700">{formatCurrency(item.avg_kilo_price)}</td>}
                        {admin && <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">{formatCurrency(item.total_value)}</td>}
                        {admin && <td className="px-4 py-3 text-right"><MinKgInput item={item} onSaved={fetchData} /></td>}
                        <td className="px-4 py-3 text-gray-500">{item.last_purchase_date || "-"}</td>
                        {admin && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => openHistory(item.product_name)} data-testid={`history-${item.product_name}`}>History</Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600" onClick={() => handleStockEdit(item)} data-testid={`table-edit-${item.product_name}`}><PencilSimple size={14} /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleStockDelete(item)} data-testid={`table-delete-${item.product_name}`}><Trash size={14} /></Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => openReduce(item)} data-testid={`table-reduce-${item.product_name}`}>
                              <ArrowDown size={12} className="mr-0.5" /> Reduce
                            </Button>
                          </div>
                        </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ====== Reduce Stock Dialog ====== */}
      <Dialog open={showReduceDialog} onOpenChange={setShowReduceDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Reduce Stock</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-semibold">Product</Label>
              <Select value={reduceProduct} onValueChange={setReduceProduct}>
                <SelectTrigger className="h-10" data-testid="reduce-product"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{stock.map((s) => <SelectItem key={s.product_name} value={s.product_name}>{s.product_name} ({s.available_kg} KG)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Kilograms to Reduce</Label>
              <Input type="number" min="0" step="0.1" value={reduceKg} onChange={(e) => setReduceKg(e.target.value)} placeholder="e.g. 10" className="h-10" data-testid="reduce-kg" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Batch Code (optional, FIFO if empty)</Label>
              <Select value={reduceBatch} onValueChange={setReduceBatch}>
                <SelectTrigger className="h-10" data-testid="reduce-batch"><SelectValue placeholder="Auto (FIFO - oldest first)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fifo">Auto FIFO (oldest first)</SelectItem>
                  {batches.filter(b => b.product_name === reduceProduct && b.kg_remaining > 0).map(b => (
                    <SelectItem key={b.batch_code} value={b.batch_code}>{b.batch_code} ({b.kg_remaining} KG avail)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Reason</Label>
              <Input value={reduceNotes} onChange={(e) => setReduceNotes(e.target.value)} placeholder="Sale, wastage, etc." className="h-10" data-testid="reduce-notes" />
            </div>
            <Button onClick={handleReduce} className="w-full h-10 bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="save-reduce-btn">
              <ArrowDown size={16} className="mr-1" /> Reduce Stock
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Edit Entry Dialog ====== */}
      <Dialog open={showEditDialog} onOpenChange={(o) => { setShowEditDialog(o); if (!o) setEditManualProduct(false); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Edit Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm font-semibold">Product Name</Label>
                <button type="button" onClick={() => { setEditManualProduct(!editManualProduct); }} className="text-[11px] text-indigo-600 font-bold hover:underline" data-testid="edit-toggle-manual">
                  {editManualProduct ? "Select from list" : "Type manually"}
                </button>
              </div>
              {editManualProduct ? (
                <Input value={editForm.product_name} onChange={(e) => setEditForm(p => ({ ...p, product_name: e.target.value }))} className="h-10" data-testid="edit-manual-product" />
              ) : (
                <Select value={editForm.product_name} onValueChange={(v) => setEditForm(p => ({ ...p, product_name: v }))}>
                  <SelectTrigger className="h-10" data-testid="edit-product"><SelectValue /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_name}>{p.product_name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold">Entry Type</Label>
              <Select value={editForm.entry_type} onValueChange={(v) => setEditForm(p => ({ ...p, entry_type: v }))}>
                <SelectTrigger className="h-10" data-testid="edit-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTRY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">KG</Label><Input type="number" min="0" step="0.1" value={editForm.kg} onChange={(e) => setEditForm(p => ({ ...p, kg: e.target.value }))} className="h-10" data-testid="edit-kg" /></div>
              <div><Label className="text-sm font-semibold">Date</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm(p => ({ ...p, date: e.target.value }))} className="h-10" data-testid="edit-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Value</Label><Input type="number" min="0" step="0.01" value={editForm.value} onChange={(e) => setEditForm(p => ({ ...p, value: e.target.value }))} className="h-10" data-testid="edit-value" /></div>
              <div><Label className="text-sm font-semibold">Kilo Price</Label><Input type="number" min="0" step="0.01" value={editForm.kilo_price} onChange={(e) => setEditForm(p => ({ ...p, kilo_price: e.target.value }))} className="h-10" data-testid="edit-kilo-price" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Batch Code</Label><Input value={editForm.batch_code} onChange={(e) => setEditForm(p => ({ ...p, batch_code: e.target.value }))} className="h-10" data-testid="edit-batch" /></div>
              <div><Label className="text-sm font-semibold">Supplier</Label><Input value={editForm.supplier} onChange={(e) => setEditForm(p => ({ ...p, supplier: e.target.value }))} className="h-10" data-testid="edit-supplier" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold">Expiry</Label><Input type="date" value={editForm.expiry_date} onChange={(e) => setEditForm(p => ({ ...p, expiry_date: e.target.value }))} className="h-10" /></div>
              <div><Label className="text-sm font-semibold">Notes</Label><Input value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} className="h-10" data-testid="edit-notes" /></div>
            </div>
            <div><Label className="text-sm font-semibold text-orange-700">Edit Remarks *</Label><Input value={editForm.remarks} onChange={(e) => setEditForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Reason for edit..." className="h-10 border-orange-200" data-testid="edit-remarks" /></div>
            <Button onClick={handleSaveEdit} className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-edit-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== History Dialog ====== */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">{historyProduct ? `${historyProduct} — History` : "All Inventory History"}</DialogTitle></DialogHeader>
          <div className="py-2">
            {history.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No entries yet</p>
            ) : (
              <div className="space-y-1.5">
                {history.map((h) => {
                  const isPurchase = h.type === "purchase";
                  const cfg = getEntryTypeConfig(h.type);
                  return (
                    <div key={h.entry_id} className={`flex items-center justify-between p-2.5 rounded-lg border text-xs ${isPurchase ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid={`history-${h.entry_id}`}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {isPurchase ? <ArrowUp size={14} className="text-green-600 flex-shrink-0" /> : <ArrowDown size={14} className="text-red-600 flex-shrink-0" />}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-bold text-gray-900 truncate">{h.product_name}</p>
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${cfg.color}`}>{cfg.label}</span>
                            {h.batch_code && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono bg-indigo-50 text-indigo-600 border border-indigo-200">{h.batch_code}</span>}
                          </div>
                          <p className="text-gray-500">{h.date} {h.notes ? `— ${h.notes}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-right">
                          <p className={`font-bold font-mono ${isPurchase ? "text-green-600" : "text-red-600"}`}>{isPurchase ? "+" : "-"}{h.kg} KG</p>
                          {h.value > 0 && <p className="text-gray-500 font-mono">{formatCurrency(h.value)}</p>}
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-indigo-500 hover:text-indigo-700" onClick={() => openEdit(h)} data-testid={`edit-entry-${h.entry_id}`}><PencilSimple size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500" onClick={() => deleteEntry(h.entry_id)} data-testid={`delete-entry-${h.entry_id}`}><Trash size={12} /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Batches View Dialog ====== */}
      <Dialog open={showBatches} onOpenChange={setShowBatches}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-bold">Inventory by Batch</DialogTitle></DialogHeader>
          <div className="py-2">
            {batches.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No batches yet. Add a purchase to create batches.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Product</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Batch Code</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">Qty Available</th>
                      {admin && <th className="px-3 py-2 text-right font-semibold text-gray-600">Buy Rate</th>}
                      {admin && <th className="px-3 py-2 text-left font-semibold text-gray-600">Supplier</th>}
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Expiry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batches.map((b) => {
                      const isLow = b.kg_remaining < 10;
                      const isExpiring = b.expiry_date && b.expiry_date <= new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];
                      return (
                        <tr key={b.entry_id} className={`hover:bg-gray-50 ${b.kg_remaining <= 0 ? "opacity-40" : ""}`}>
                          <td className="px-3 py-2 font-semibold text-gray-900">{b.product_name}</td>
                          <td className="px-3 py-2 font-mono text-indigo-600 font-bold">{b.batch_code}</td>
                          <td className="px-3 py-2 text-right">
                            <span className={`font-bold font-mono ${b.kg_remaining <= 0 ? "text-gray-400" : isLow ? "text-red-600" : "text-green-600"}`}>{b.kg_remaining} KG</span>
                            {isLow && b.kg_remaining > 0 && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-bold">LOW</span>}
                          </td>
                          {admin && <td className="px-3 py-2 text-right font-mono">{formatCurrency(b.kilo_price)}/kg</td>}
                          {admin && <td className="px-3 py-2 text-gray-500">{b.supplier || "-"}</td>}
                          <td className="px-3 py-2 text-gray-500">{b.date}</td>
                          <td className={`px-3 py-2 ${isExpiring ? "text-red-600 font-bold" : "text-gray-400"}`}>{b.expiry_date || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
